import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { api, eur } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Plus, X, Trash2, MapPin, User, Phone, Mail, MessageSquare, CalendarClock, StickyNote, Clock } from "lucide-react";

const STAGES = [
  { key: "por_contactar", label: "Por Contactar", color: "#3B82F6" },
  { key: "contactado", label: "Contactado", color: "#8B5CF6" },
  { key: "visitas", label: "Visitas", color: "#F59E0B" },
  { key: "propostas", label: "Propostas", color: "#C9A84C" },
  { key: "perdido", label: "Perdido", color: "#EF4444" },
  { key: "representar_outro", label: "Representar Noutro Imóvel", color: "#10B981" },
];

const EMPTY = { title: "", client_name: "", location: "", typology: "", estimated_value: "", stage: "por_contactar", property_image: "", notes: "", contacts: [] };

const CONTACT_TYPES = [
  { key: "chamada", label: "Chamada", icon: Phone },
  { key: "email", label: "Email", icon: Mail },
  { key: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { key: "visita", label: "Visita", icon: CalendarClock },
  { key: "nota", label: "Nota", icon: StickyNote },
];

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [contactNote, setContactNote] = useState("");
  const [contactType, setContactType] = useState("chamada");

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setModal(true); };
  const openEdit = (l) => { setForm({ ...l, estimated_value: l.estimated_value || "" }); setEditing(l.id); setModal(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, estimated_value: parseFloat(form.estimated_value) || 0 };
    try {
      if (editing) await api.patch(`/leads/${editing}`, payload);
      else await api.post("/leads", payload);
      toast.success(editing ? "Lead atualizada" : "Lead criada");
      setModal(false); load();
    } catch (err) { toast.error("Erro ao guardar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar esta lead?")) return;
    await api.delete(`/leads/${id}`); toast.success("Lead eliminada"); load();
  };

  const addContact = async () => {
    if (!contactNote.trim() || !editing) return;
    try {
      const { data } = await api.post(`/leads/${editing}/contacts`, { type: contactType, note: contactNote });
      setForm((f) => ({ ...f, contacts: data.contacts }));
      setContactNote(""); toast.success("Contacto registado"); load();
    } catch { toast.error("Erro ao registar contacto"); }
  };

  const onDrop = async (stage) => {
    if (!dragId) return;
    const lead = leads.find((l) => l.id === dragId);
    if (lead && lead.stage !== stage) {
      setLeads((prev) => prev.map((l) => (l.id === dragId ? { ...l, stage } : l)));
      try { await api.patch(`/leads/${dragId}`, { stage }); toast.success("Fase atualizada"); }
      catch { load(); }
    }
    setDragId(null);
  };

  return (
    <Layout title="Gestão de Leads">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-400">Pipeline estilo Kanban — arraste os cartões entre fases</p>
        {!["suporte", "leitura", "assistente"].includes(user?.role) && <button onClick={openNew} data-testid="new-lead-btn"
          className="flex items-center gap-2 bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold px-4 py-2.5 rounded-lg transition-all hover:scale-[1.02]">
          <Plus size={18} /> Nova Lead
        </button>}
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((s) => {
          const items = leads.filter((l) => l.stage === s.key);
          return (
            <div key={s.key} data-testid={`kanban-stage-${s.key}`}
              onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(s.key)}
              className="bg-[#112233]/80 border border-[#1E3A56] rounded-xl p-3 min-w-[280px] w-[280px] flex-shrink-0 flex flex-col gap-3">
              <div className="flex items-center justify-between pb-2 border-b border-[#1E3A56]">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                  <span className="text-sm font-semibold text-slate-200">{s.label}</span>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#0D1B2A] text-slate-400 border border-[#1E3A56]">{items.length}</span>
              </div>
              <div className="space-y-3 min-h-[100px]">
                {items.map((l) => (
                  <div key={l.id} draggable onDragStart={() => setDragId(l.id)} onClick={() => openEdit(l)}
                    data-testid={`lead-card-${l.id}`}
                    className="bg-[#152A3E] border border-[#1E3A56] hover:border-[#C9A84C]/60 rounded-lg overflow-hidden cursor-grab active:cursor-grabbing shadow-md hover:-translate-y-0.5 transition-all">
                    {l.property_image && <img src={l.property_image} alt="" className="w-full h-24 object-cover" />}
                    <div className="p-3">
                      <div className="text-sm font-semibold text-slate-100 truncate">{l.title}</div>
                      <div className="text-xs text-[#E0C574] mt-0.5">{eur(l.estimated_value)}</div>
                      <div className="flex items-center gap-1 text-[11px] text-slate-400 mt-1"><User size={11} /> {l.client_name}</div>
                      {l.location && <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-0.5"><MapPin size={11} /> {l.location}</div>}
                      <button onClick={(e) => { e.stopPropagation(); remove(l.id); }}
                        className="mt-2 text-[11px] text-red-400/70 hover:text-red-400 flex items-center gap-1"><Trash2 size={11} /> Eliminar</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setModal(false)}>
          <div className="card-almar w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lead-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl text-[#C9A84C]">{editing ? "Editar Lead" : "Nova Lead"}</h3>
              <button onClick={() => setModal(false)}><X size={20} className="text-slate-400" /></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <input className="input-almar" placeholder="Título do imóvel" required data-testid="lead-title-input"
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <input className="input-almar" placeholder="Nome do cliente" required data-testid="lead-client-input"
                value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input className="input-almar" placeholder="Localização" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                <input className="input-almar" placeholder="Tipologia (T2, Villa...)" value={form.typology} onChange={(e) => setForm({ ...form, typology: e.target.value })} />
              </div>
              <input className="input-almar" type="number" placeholder="Valor estimado (€)" data-testid="lead-value-input"
                value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} />
              <input className="input-almar" placeholder="URL da imagem do imóvel" value={form.property_image} onChange={(e) => setForm({ ...form, property_image: e.target.value })} />
              <select className="input-almar" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="lead-stage-select">
                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <textarea className="input-almar" rows={2} placeholder="Notas / histórico" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <button type="submit" data-testid="lead-save-btn"
                className="w-full bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold py-2.5 rounded-lg transition-all">
                {editing ? "Guardar Alterações" : "Criar Lead"}
              </button>
            </form>
            {editing && (
              <div className="mt-5 pt-5 border-t border-[#1E3A56]" data-testid="contacts-timeline">
                <h4 className="text-xs uppercase tracking-wider text-[#C9A84C] mb-3 flex items-center gap-2"><Clock size={14} /> Histórico de Contactos</h4>
                <div className="flex gap-2 mb-3">
                  <select className="input-almar w-32" value={contactType} onChange={(e) => setContactType(e.target.value)} data-testid="contact-type-select">
                    {CONTACT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                  <input className="input-almar flex-1" placeholder="Registar contacto ou nota..." value={contactNote}
                    onChange={(e) => setContactNote(e.target.value)} data-testid="contact-note-input"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addContact(); } }} />
                  <button type="button" onClick={addContact} data-testid="add-contact-btn"
                    className="bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold px-4 rounded-lg transition-all">Registar</button>
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {(form.contacts || []).slice().reverse().map((c) => {
                    const meta = CONTACT_TYPES.find((t) => t.key === c.type) || CONTACT_TYPES[4];
                    const Icon = meta.icon;
                    return (
                      <div key={c.id} className="flex gap-3 p-3 rounded-lg bg-[#0D1B2A] border border-[#1E3A56]">
                        <span className="w-8 h-8 rounded-lg bg-[#C9A84C]/15 flex items-center justify-center text-[#C9A84C] flex-shrink-0"><Icon size={15} /></span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-semibold text-slate-200">{meta.label}</span>
                            <span className="text-[10px] text-slate-500">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
                          </div>
                          <p className="text-sm text-slate-300 mt-0.5 break-words">{c.note}</p>
                          {c.author && <span className="text-[10px] text-slate-500">— {c.author}</span>}
                        </div>
                      </div>
                    );
                  })}
                  {(!form.contacts || form.contacts.length === 0) && <p className="text-xs text-slate-500 text-center py-3">Sem contactos registados.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
